import React, { useState, useEffect, useCallback } from 'react';
import { X, Search, Filter, AlertTriangle, CheckCircle, Clock, Calendar, User, RefreshCw, ChevronDown, ExternalLink, Loader2, Maximize2, Brain, TrendingUp, FileText, Activity, Zap } from 'lucide-react';
import Sidebar from '../components/ui/Sidebar';
import { IncidentCardSkeleton } from '../components/ui/Skeleton';
import { toast } from 'sonner';
import { getCamundaService } from '../../infrastructure/camunda';
import type { Incident } from '../../infrastructure/camunda';

interface IncidentItem {
  id: string;
  processName: string;
  processInstanceId: string;
  processDefinitionId: string;
  executionId: string;
  activityId: string;
  incidentType: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: 'open' | 'resolved' | 'acknowledged';
  created: string;
  assignee?: string;
  assigneeAvatar?: string;
  description: string;
  rootCause?: string;
  jobDefinitionId?: string;
  annotation?: string;
}

const IncidentsPage: React.FC = () => {
  const [selectedIncident, setSelectedIncident] = useState<IncidentItem | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTimeFilter, setSelectedTimeFilter] = useState('24 hours');
  const [showFilters, setShowFilters] = useState(false);
  const [showFullDialog, setShowFullDialog] = useState(false);
  const [showAnalyzeDialog, setShowAnalyzeDialog] = useState(false);
  const [incidents, setIncidents] = useState<IncidentItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [incidentDetails, setIncidentDetails] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const camunda = getCamundaService();

  // Format date relative to now
  const formatRelativeTime = (dateString: string | null): string => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    return `${diffDays} days ago`;
  };

  // Pagination state
  const pageSize = 10;
  const [currentPage, setCurrentPage] = useState(0);

  // Fetch incidents from Camunda with pagination
  const fetchIncidents = useCallback(async (page: number = 0) => {
    try {
      setLoading(true);

      // Get total count first
      const countResult = await camunda.incident.getCount({});
      setTotalCount(countResult.count);

      // Fetch paginated incidents
      const camundaIncidents = await camunda.incident.getList({
        sortBy: 'incidentTimestamp',
        sortOrder: 'desc',
        firstResult: page * pageSize,
        maxResults: pageSize,
      });

      const incidentItems: IncidentItem[] = camundaIncidents.map((incident: Incident) => {
        // Determine severity based on incident type
        let severity: IncidentItem['severity'] = 'medium';
        if (incident.incidentType === 'failedJob') severity = 'high';
        if (incident.incidentType === 'failedExternalTask') severity = 'critical';

        return {
          id: incident.id,
          processName: incident.processDefinitionId?.split(':')[0] || 'Unknown Process',
          processInstanceId: incident.processInstanceId || '',
          processDefinitionId: incident.processDefinitionId || '',
          executionId: incident.executionId || '',
          activityId: incident.activityId || '',
          incidentType: incident.incidentType || 'Unknown',
          severity,
          status: 'open',
          created: formatRelativeTime(incident.incidentTimestamp),
          description: incident.incidentMessage || 'No description available',
          rootCause: incident.causeIncidentId ? `Caused by incident: ${incident.causeIncidentId}` : undefined,
          jobDefinitionId: incident.jobDefinitionId || undefined,
          annotation: incident.annotation || undefined,
        };
      });

      setIncidents(incidentItems);
    } catch (error) {
      console.error('Error fetching incidents:', error);
      toast.error('Failed to fetch incidents');
    } finally {
      setLoading(false);
    }
  }, [camunda]);

  // Fetch detailed incident information including stacktrace
  const fetchIncidentDetails = useCallback(async (incidentId: string) => {
    try {
      setLoadingDetails(true);
      const details = await camunda.incident.getById(incidentId);
      setIncidentDetails(details);

      // If there's a job associated, fetch the stacktrace
      if (details.jobDefinitionId) {
        try {
          const jobs = await camunda.job.getList({
            processInstanceId: details.processInstanceId || undefined,
            withException: true,
          });

          if (jobs.length > 0 && jobs[0].id) {
            try {
              const stacktrace = await camunda.job.getStacktrace(jobs[0].id);
              setIncidentDetails((prev: any) => ({
                ...prev,
                stacktrace: stacktrace
              }));
            } catch (stacktraceError) {
              console.error('Error fetching stacktrace:', stacktraceError);
              // Set incident details without stacktrace
              setIncidentDetails((prev: any) => ({
                ...prev,
                stacktrace: null,
                stacktraceError: 'Failed to fetch stacktrace'
              }));
            }
          }
        } catch (jobError) {
          console.error('Error fetching job details:', jobError);
          // Set incident details without job info
          setIncidentDetails((prev: any) => ({
            ...prev,
            stacktrace: null,
            stacktraceError: 'No associated job found'
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching incident details:', error);
      setIncidentDetails({
        error: 'Failed to fetch incident details'
      });
    } finally {
      setLoadingDetails(false);
    }
  }, [camunda]);

  // Resolve an incident
  const handleResolveIncident = async () => {
    if (!selectedIncident) return;
    try {
      setResolving(true);
      await camunda.incident.resolve(selectedIncident.id);
      toast.success('Incident resolved successfully');
      fetchIncidents();
      setSelectedIncident(null);
    } catch (error) {
      console.error('Error resolving incident:', error);
      toast.error('Failed to resolve incident');
    } finally {
      setResolving(false);
    }
  };

  // Retry failed job
  const handleRetryJob = async () => {
    if (!selectedIncident?.jobDefinitionId) {
      toast.error('No job associated with this incident');
      return;
    }
    try {
      setRetrying(true);
      // Find the job and set retries
      const jobs = await camunda.job.getList({
        processInstanceId: selectedIncident.processInstanceId,
        withException: true,
      });

      if (jobs.length > 0) {
        await camunda.job.setRetries(jobs[0].id, 1);
        toast.success('Job retry scheduled');
        fetchIncidents();
      } else {
        toast.error('No failed job found');
      }
    } catch (error) {
      console.error('Error retrying job:', error);
      toast.error('Failed to retry job');
    } finally {
      setRetrying(false);
    }
  };

  // Initial load and page change
  useEffect(() => {
    fetchIncidents(currentPage);
  }, [fetchIncidents, currentPage]);

  // Filter incidents by search query (client-side filter on currently loaded page)
  const filteredIncidents = searchQuery
    ? incidents.filter(incident =>
      incident.processName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      incident.incidentType.toLowerCase().includes(searchQuery.toLowerCase()) ||
      incident.description.toLowerCase().includes(searchQuery.toLowerCase())
    )
    : incidents;


  const getSeverityColor = (severity: IncidentItem['severity']) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'low':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusColor = (status: IncidentItem['status']) => {
    switch (status) {
      case 'open':
        return 'bg-red-100 text-red-700';
      case 'acknowledged':
        return 'bg-yellow-100 text-yellow-700';
      case 'resolved':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status: IncidentItem['status']) => {
    switch (status) {
      case 'open':
        return <AlertTriangle size={16} className="text-red-600" />;
      case 'acknowledged':
        return <Clock size={16} className="text-yellow-600" />;
      case 'resolved':
        return <CheckCircle size={16} className="text-green-600" />;
      default:
        return null;
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />

      <div className="flex-1 ml-16 mr-4 mt-4 mb-4">
        <div className="h-full bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col">
          <header className="bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle size={20} className="text-red-600" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">Incidents</h1>
                  <p className="text-sm text-gray-500">Analyze and resolve process incidents</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search incidents..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 w-64 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white"
                  />
                </div>
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                  {['24h', '7d', '30d'].map((filter) => (
                    <button
                      key={filter}
                      onClick={() => setSelectedTimeFilter(filter)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${selectedTimeFilter === filter
                        ? 'bg-white text-red-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                      {filter}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                >
                  <Filter size={16} />
                  Filters
                  <ChevronDown size={14} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </button>
                <button
                  onClick={() => fetchIncidents()}
                  className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  disabled={loading}
                >
                  <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                </button>
              </div>
            </div>
          </header>

          <div className="flex flex-1 overflow-hidden">
            {/* Main Content */}
            <div className="flex-1 p-6 overflow-auto">
              {/* Stats Bar */}
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-600">{totalCount} incidents found</span>
              </div>

              {/* Incident List */}
              <div className="bg-white rounded-lg border border-gray-200">
                {loading ? (
                  <>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <IncidentCardSkeleton key={i} />
                    ))}
                  </>
                ) : filteredIncidents.length === 0 ? (
                  <div className="flex items-center justify-center p-8 text-gray-500">
                    No incidents found
                  </div>
                ) : (
                  filteredIncidents.map((incident) => (
                    <div
                      key={incident.id}
                      className="p-4 border-b border-gray-200 hover:bg-gray-50 cursor-pointer"
                      onClick={() => {
                        setSelectedIncident(incident);
                        setIncidentDetails(null);
                        setLoadingDetails(false);
                        fetchIncidentDetails(incident.id);
                      }}
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 mt-1">
                          {getStatusIcon(incident.status)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <h3 className="text-base font-medium text-gray-900">{incident.incidentType}</h3>
                              <p className="text-sm text-gray-600">{incident.description}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded text-xs font-medium border ${getSeverityColor(incident.severity)}`}>
                                {incident.severity.charAt(0).toUpperCase() + incident.severity.slice(1)}
                              </span>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(incident.status)}`}>
                                {incident.status.charAt(0).toUpperCase() + incident.status.slice(1)}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              {incident.assignee && (
                                <div className="flex items-center gap-2">
                                  {incident.assigneeAvatar ? (
                                    <img
                                      src={incident.assigneeAvatar}
                                      alt={incident.assignee}
                                      className="w-5 h-5 rounded-full"
                                    />
                                  ) : (
                                    <User size={16} className="text-gray-400" />
                                  )}
                                  <span>{incident.assignee}</span>
                                </div>
                              )}
                              <div className="flex items-center gap-1">
                                <Calendar size={14} />
                                <span>Created {incident.created}</span>
                              </div>
                            </div>
                            <div className="text-sm text-gray-500">
                              {incident.processName} • Instance {incident.processInstanceId}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}

                {/* Pagination */}
                <div className="flex items-center justify-between p-4 border-t border-gray-100">
                  <div className="text-sm text-gray-500">
                    Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalCount)} of {totalCount}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                      disabled={currentPage === 0}
                      className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`px-3 py-1.5 text-sm rounded-lg ${currentPage === i
                          ? 'bg-red-600 text-white'
                          : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                          }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                      disabled={currentPage >= totalPages - 1}
                      className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Sidebar - Incident Details with Transition */}
            <div
              className={`bg-white border-l border-gray-200 flex flex-col transition-all duration-300 ease-in-out ${selectedIncident ? 'w-96 opacity-100' : 'w-0 opacity-0 overflow-hidden'
                }`}
            >
              {selectedIncident && (
                <>
                  {/* Incident Header */}
                  <div className="p-4 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {getStatusIcon(selectedIncident.status)}
                        <h2 className="text-base font-semibold text-gray-900 truncate">{selectedIncident.incidentType}</h2>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setShowFullDialog(true)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Open full details"
                        >
                          <Maximize2 size={16} />
                        </button>
                        <button
                          onClick={() => setSelectedIncident(null)}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{selectedIncident.description}</p>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getSeverityColor(selectedIncident.severity)}`}>
                        {selectedIncident.severity.charAt(0).toUpperCase() + selectedIncident.severity.slice(1)} Severity
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedIncident.status)}`}>
                        {selectedIncident.status.charAt(0).toUpperCase() + selectedIncident.status.slice(1)}
                      </span>
                    </div>
                  </div>

                  {/* Scrollable Content */}
                  <div className="flex-1 overflow-y-auto scrollbar-visible" style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f1f5f9' }}>
                    {/* Incident Details */}
                    <div className="p-6 border-b border-gray-200">
                      <h3 className="text-sm font-medium text-gray-900 mb-4">Incident Details</h3>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-gray-500">Process Name</label>
                          <p className="text-sm text-gray-900">{selectedIncident.processName}</p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Process Instance ID</label>
                          <p className="text-sm text-gray-900 font-mono">{selectedIncident.processInstanceId}</p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Created</label>
                          <p className="text-sm text-gray-900">{selectedIncident.created}</p>
                        </div>
                        {selectedIncident.assignee && (
                          <div>
                            <label className="text-xs text-gray-500">Assigned To</label>
                            <div className="flex items-center gap-2 mt-1">
                              {selectedIncident.assigneeAvatar ? (
                                <img
                                  src={selectedIncident.assigneeAvatar}
                                  alt={selectedIncident.assignee}
                                  className="w-5 h-5 rounded-full"
                                />
                              ) : (
                                <User size={16} className="text-gray-400" />
                              )}
                              <span className="text-sm text-gray-900">{selectedIncident.assignee}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Root Cause */}
                    {selectedIncident.rootCause && (
                      <div className="p-6 border-b border-gray-200">
                        <h3 className="text-sm font-medium text-gray-900 mb-4">Root Cause Analysis</h3>
                        <p className="text-sm text-gray-600">{selectedIncident.rootCause}</p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="p-6">
                      <h3 className="text-sm font-medium text-gray-900 mb-4">Actions</h3>
                      <div className="space-y-3">
                        <button
                          onClick={handleResolveIncident}
                          disabled={resolving}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                        >
                          {resolving ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <CheckCircle size={16} />
                          )}
                          Resolve Incident
                        </button>
                        <button className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                          <Clock size={16} />
                          Acknowledge
                        </button>
                        <button
                          onClick={handleRetryJob}
                          disabled={retrying}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
                        >
                          {retrying ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <RefreshCw size={16} />
                          )}
                          Retry Failed Job
                        </button>
                        <button className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                          <ExternalLink size={16} />
                          View in Processes
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Full Screen Incident Dialog */}
      {showFullDialog && selectedIncident && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowFullDialog(false)}>
          <div
            className="bg-white rounded-2xl shadow-2xl w-[90vw] max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Dialog Header */}
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {getStatusIcon(selectedIncident.status)}
                  <h2 className="text-xl font-semibold text-gray-900">{selectedIncident.incidentType}</h2>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${getSeverityColor(selectedIncident.severity)}`}>
                    {selectedIncident.severity.charAt(0).toUpperCase() + selectedIncident.severity.slice(1)} Severity
                  </span>
                  <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${getStatusColor(selectedIncident.status)}`}>
                    {selectedIncident.status.charAt(0).toUpperCase() + selectedIncident.status.slice(1)}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowAnalyzeDialog(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg text-sm font-medium hover:from-purple-700 hover:to-blue-700 transition-all"
                >
                  <Brain size={16} />
                  Analyze with AI
                </button>
                <button
                  onClick={() => setShowFullDialog(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Dialog Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-3 gap-6">
                {/* Left Column - Incident Details */}
                <div className="col-span-1 space-y-6">
                  {/* Description */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Description</h4>
                    <p className="text-sm text-gray-700">{selectedIncident.description}</p>
                  </div>

                  {/* Incident Details */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Incident Details</h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar size={14} className="text-gray-400" />
                        <span className="text-gray-600">Process:</span>
                        <span className="text-gray-900 font-medium">{selectedIncident.processName}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Activity size={14} className="text-gray-400" />
                        <span className="text-gray-600">Instance ID:</span>
                        <span className="text-gray-900 font-mono text-xs">{selectedIncident.processInstanceId}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock size={14} className="text-gray-400" />
                        <span className="text-gray-600">Created:</span>
                        <span className="text-gray-900">{selectedIncident.created}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Zap size={14} className="text-gray-400" />
                        <span className="text-gray-600">Activity:</span>
                        <span className="text-gray-900 font-mono text-xs">{selectedIncident.activityId}</span>
                      </div>
                    </div>
                  </div>

                  {/* Root Cause */}
                  {selectedIncident.rootCause && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Root Cause Analysis</h4>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-sm text-red-800">{selectedIncident.rootCause}</p>
                      </div>
                    </div>
                  )}

                  {/* Technical Details */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Technical Details</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Job Definition ID:</span>
                        <span className="text-gray-900 font-mono text-xs truncate max-w-[180px]" title={selectedIncident.jobDefinitionId}>
                          {selectedIncident.jobDefinitionId || 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Execution ID:</span>
                        <span className="text-gray-900 font-mono text-xs truncate max-w-[180px]" title={selectedIncident.executionId}>
                          {selectedIncident.executionId}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Timeline & Actions */}
                <div className="col-span-2 space-y-6">
                  {/* Timeline */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-4">Error Details</h4>
                    {loadingDetails ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 size={24} className="animate-spin text-blue-600" />
                        <span className="ml-2 text-gray-600">Loading error details...</span>
                      </div>
                    ) : incidentDetails?.error ? (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                        <AlertTriangle size={24} className="mx-auto text-red-600 mb-2" />
                        <p className="text-sm text-red-800">{incidentDetails.error}</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex gap-4">
                          <div className="flex-shrink-0 w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-gray-900">Error Message</span>
                              <span className="text-xs text-gray-500">{selectedIncident.created}</span>
                            </div>
                            <p className="text-sm text-gray-600 font-mono bg-red-50 p-3 rounded border border-red-200">
                              {incidentDetails?.incidentMessage || selectedIncident.description}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-4">
                          <div className="flex-shrink-0 w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-gray-900">Stack Trace</span>
                              <span className="text-xs text-gray-500">2 minutes ago</span>
                            </div>
                            <div className="text-sm text-gray-600 font-mono bg-gray-900 text-green-400 p-3 rounded overflow-auto max-h-64 max-w-[700px]">
                              {incidentDetails?.stacktrace ? (
                                <pre className="whitespace-pre text-xs">
                                  {incidentDetails.stacktrace}
                                </pre>
                              ) : incidentDetails?.stacktraceError ? (
                                <div className="text-yellow-400 text-xs">
                                  ⚠️ {incidentDetails.stacktraceError}
                                </div>
                              ) : (
                                <div className="text-gray-400 text-xs">No stacktrace available for this incident type</div>
                              )}
                            </div>
                          </div>
                        </div>
                        {incidentDetails?.activityId && (
                          <div className="flex gap-4">
                            <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium text-gray-900">Failed Activity</span>
                                <span className="text-xs text-gray-500">Process Context</span>
                              </div>
                              <p className="text-sm text-gray-600">
                                Error occurred in activity: <span className="font-mono bg-blue-50 px-2 py-1 rounded">{incidentDetails.activityId}</span>
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-4">Recommended Actions</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={handleResolveIncident}
                        disabled={resolving}
                        className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                      >
                        {resolving ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <CheckCircle size={16} />
                        )}
                        Resolve Incident
                      </button>
                      <button className="flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                        <Clock size={16} />
                        Acknowledge
                      </button>
                      <button
                        onClick={handleRetryJob}
                        disabled={retrying}
                        className="flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
                      >
                        {retrying ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <RefreshCw size={16} />
                        )}
                        Retry Failed Job
                      </button>
                      <button className="flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
                        <ExternalLink size={16} />
                        View in Processes
                      </button>
                    </div>
                  </div>

                  {/* Related Incidents */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-4">Related Incidents</h4>
                    <div className="bg-gray-50 rounded-lg p-4 text-center text-sm text-gray-500">
                      No related incidents found
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Analyze AI Dialog Overlay */}
      {showAnalyzeDialog && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60]" onClick={() => setShowAnalyzeDialog(false)}>
          <div
            className="bg-white rounded-2xl shadow-2xl w-[80vw] max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Analyze Dialog Header */}
            <div className="p-6 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-purple-50 to-blue-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg">
                  <Brain size={20} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">AI Incident Analysis</h2>
                  <p className="text-sm text-gray-600">Powered by advanced machine learning algorithms</p>
                </div>
              </div>
              <button
                onClick={() => setShowAnalyzeDialog(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Analyze Dialog Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {analyzing ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="relative">
                    <div className="w-16 h-16 border-4 border-purple-200 rounded-full"></div>
                    <div className="w-16 h-16 border-4 border-purple-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mt-4">Analyzing Incident</h3>
                  <p className="text-sm text-gray-500 mt-2">AI is processing incident data and generating insights...</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Analysis Summary */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <TrendingUp size={20} className="text-green-600" />
                      Analysis Summary
                    </h3>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">87%</div>
                          <div className="text-sm text-gray-600">Confidence Score</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">3</div>
                          <div className="text-sm text-gray-600">Root Causes</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-purple-600">5</div>
                          <div className="text-sm text-gray-600">Recommendations</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Root Cause Analysis */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Activity size={20} className="text-red-600" />
                      Root Cause Analysis
                    </h3>
                    <div className="space-y-3">
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</div>
                          <div>
                            <h4 className="font-medium text-gray-900 mb-1">Database Connection Timeout</h4>
                            <p className="text-sm text-gray-600">The incident was triggered by a database connection timeout during the payment processing step. Connection pool exhaustion detected.</p>
                            <div className="mt-2 flex items-center gap-2">
                              <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">High Impact</span>
                              <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-medium">Frequent</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-6 h-6 bg-orange-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</div>
                          <div>
                            <h4 className="font-medium text-gray-900 mb-1">Insufficient Error Handling</h4>
                            <p className="text-sm text-gray-600">The service lacks proper retry mechanisms and circuit breaker patterns for external API calls.</p>
                            <div className="mt-2 flex items-center gap-2">
                              <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-medium">Medium Impact</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recommendations */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Zap size={20} className="text-blue-600" />
                      Recommended Actions
                    </h3>
                    <div className="space-y-3">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</div>
                          <div>
                            <h4 className="font-medium text-gray-900 mb-1">Increase Database Connection Pool</h4>
                            <p className="text-sm text-gray-600">Scale up the database connection pool from 10 to 25 connections to handle peak load scenarios.</p>
                            <div className="mt-2">
                              <button className="px-3 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700">
                                Apply Fix
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</div>
                          <div>
                            <h4 className="font-medium text-gray-900 mb-1">Implement Circuit Breaker Pattern</h4>
                            <p className="text-sm text-gray-600">Add resilience patterns with exponential backoff for external service calls.</p>
                            <div className="mt-2">
                              <button className="px-3 py-1 bg-purple-600 text-white rounded text-xs font-medium hover:bg-purple-700">
                                View Implementation Guide
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Prevention Measures */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <FileText size={20} className="text-green-600" />
                      Prevention Measures
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-green-600 mt-0.5" />
                          <span className="text-sm text-gray-700">Set up monitoring alerts for database connection pool usage above 80%</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-green-600 mt-0.5" />
                          <span className="text-sm text-gray-700">Implement automated health checks for external dependencies</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-green-600 mt-0.5" />
                          <span className="text-sm text-gray-700">Schedule regular load testing to identify bottlenecks</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Analyze Dialog Footer */}
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowAnalyzeDialog(false)}
                className="px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setAnalyzing(true);
                  setTimeout(() => setAnalyzing(false), 3000);
                }}
                className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg text-sm font-medium hover:from-purple-700 hover:to-blue-700 transition-all"
              >
                Re-analyze
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncidentsPage;
